import { AxiosResponse } from "axios";
import axios from "utils/axios";
import { API_BASE_URL, API_SCHEDULE } from "utils/constants";

export interface Schedule {
  id: string | null;
  schedulePlanId?: string | null;
  contactId: string;
  contactName: string;
  responsibleId: string;
  observation: string;
  rentalData: RentalData;
  status: string;
  type: string;
  timeFromTo: string;
  whenDate: Date;
}

export interface SchedulePaymentChargeResponse {
  id: string;
  checkoutSessionId: string;
  paymentIntentId: string | null;
  paymentUrl: string;
  expiresAt: string;
  status: "PENDING" | "PAID" | "EXPIRED" | "CANCELED";
}

/** Shape used when reading schedules from the API (query / getById). */
export interface ScheduleResponse {
  id: string | null;
  schedulePlanId?: string | null;
  contactId: string;
  contactName: string;
  contacts: ScheduleContactRequest[];
  responsibleId: string | number;
  observation: string;
  rentalData: RentalData;
  status: string;
  type: string;
  timeFromTo: string;
  whenDate: Date;
  history?: { date: number; message: string }[];
  paymentCharge?: SchedulePaymentChargeResponse | null;
}

export interface MultiScheduleCreate {
  schedules: CreateScheduleRequest[];
  notify: boolean;
}

export interface ScheduleContactRequest {
  id: string;
  name: string;
}

export interface CreateScheduleRequest {
  contacts: ScheduleContactRequest[];
  responsibleId: string;
  observation: string;
  rentalData: RentalData;
  status: string;
  type: string;
  timeFromTo: string;
  whenDate: Date;
}

interface RentalData {
  sportCourtId: string;
  sportCourtValue: number;
  sportCourtRentalTime: number;
  rentalQuantity: number;
}

export interface ScheduleQuery {
  limit: number;
  query: Record<string, string | number | boolean | string[] | undefined>;
  page: number;
}

export interface ScheduleQueryResponse {
  totalPages: number;
  totalElements: number;
  entities: Schedule[];
}

export interface ScheduleCountResponse {
  count: number;
}

const getById = (id: string): Promise<AxiosResponse<ScheduleResponse>> =>
  axios.get<ScheduleResponse>(`${API_BASE_URL}${API_SCHEDULE.BASE_URL}/${id}`);
const save = (payload: MultiScheduleCreate) =>
  axios.post(`${API_BASE_URL}${API_SCHEDULE.BASE_URL}`, payload);
const update = (id: string, schedule: Schedule) =>
  axios.put(`${API_BASE_URL}${API_SCHEDULE.BASE_URL}/${id}`, schedule);
const remove = (id: string) =>
  axios.delete(`${API_BASE_URL}${API_SCHEDULE.BASE_URL}/${id}`);
const query = (
  queryPayload: ScheduleQuery
): Promise<AxiosResponse<ScheduleQueryResponse>> =>
  axios.post<ScheduleQueryResponse>(
    `${API_BASE_URL}${API_SCHEDULE.BASE_URL}${API_SCHEDULE.QUERY_URL}`,
    queryPayload
  );
const count = (
  queryPayload: ScheduleQuery
): Promise<AxiosResponse<ScheduleCountResponse>> =>
  axios.post<ScheduleCountResponse>(
    `${API_BASE_URL}${API_SCHEDULE.BASE_URL}${API_SCHEDULE.COUNT_URL}`,
    queryPayload
  );

const createCharge = (id: string): Promise<AxiosResponse<SchedulePaymentChargeResponse>> =>
  axios.post<SchedulePaymentChargeResponse>(
    `${API_BASE_URL}${API_SCHEDULE.BASE_URL}/${id}/charge`
  );

const scheduleAPI = {
  getById,
  save,
  update,
  remove,
  query,
  count,
  createCharge,
};

export default scheduleAPI;
